import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ClipboardCheck, Loader2, ChevronRight, Plus } from 'lucide-react';
import Header from '../../components/Header';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrapArray } from '../../lib/api-envelope';
import { formatCalendarDate, formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import {
  inspectionFormTypeLabel,
  inspectionResultLabel,
  inspectionStatusLabel,
  isResumableDraft,
} from '../../lib/inspection-labels';
import { inspectionDetailPath, inspectionResumePath } from '../../lib/inspection-surface';
import { useBack } from '../../hooks/useBack';

/**
 * A hand's own inspections, on the casing field surface.
 *
 * THE BUG THIS CLOSES. TNT's crew complete an inspection and cannot see it
 * again. The record was never lost — every one of their 18 submissions is in
 * `equipment_inspection_submissions`, and the endpoint that lists them is
 * ungated, so a field_worker could already read the whole tenant's. What was
 * missing was a screen on their surface and a way to ask for their own.
 *
 * THE CREW'S WORK, NOT YOURS. `scope=crew` narrows server-side to
 * `submitted_by = me OR job_id IN (jobs I am crewed on)`. Two tong hands on
 * one job both run inspections, and the question this screen answers is
 * whether the GEAR was checked — not what one person filed. It is still not a
 * whole-company list: an inspection on a job you are not on does not appear.
 *
 * THE UNION IS LOAD-BEARING, IN BOTH DIRECTIONS. A bare job filter would be
 * wrong twice over, and both failures render as an empty screen rather than as
 * an error:
 *   - rows predating API 0344 have `job_id` NULL for good. `job_location` is
 *     free text — its distinct live values include 'Shop' and 'Cevovus' — so
 *     no backfill from it is honest, and a job-only filter would drop every
 *     one of them out of its author's list.
 *   - THREE OF THE SIX DOORS into the form start with no equipment and
 *     therefore no job: the Safety quick action, the More menu, and the
 *     driver surface. A job-less inspection is a PERMANENT state, not a
 *     transition on the way to acquiring a job. `submitted_by` is what keeps
 *     those visible to the person who filed them.
 *
 * SO A ROW WITHOUT A JOB MUST READ AS NORMAL, NOT AS INCOMPLETE. There is no
 * job chip, no em dash, and no "No job" label — absent renders as absent. A
 * placeholder here would put a permanent blemish on every inspection filed
 * from half the doors in the app.
 *
 * DRAFTS ARE HERE ON PURPOSE. The form mints a draft row as soon as a type and
 * an equipment are chosen so the photo uploader has a UUID, then holds the id
 * in component state and never reads it back — so closing the form orphans the
 * row. This is the only screen in the product that can open one again.
 */
export default function FieldInspectionsScreen() {
  const navigate = useNavigate();
  const goBack = useBack('/field');
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  // Used only to decide whether to NAME the filer on a row. A null id (a
  // render before /auth/me resolves) makes every row show its submitter,
  // which is over-labelling rather than a wrong claim about who filed it.
  const currentUserId = user?.id ?? null;

  const { data, isLoading, isError, error } = useQuery({
    // The key carries the scope. Reusing the old 'mine' key would serve the
    // cached own-only list to a screen that now asks a wider question, and the
    // difference is invisible — a shorter list of real rows, not an error.
    queryKey: ['casing-inspection-submissions', 'crew'],
    // No `status` — the absence is the request. Under `scope=crew` the API
    // returns YOUR drafts alongside submitted work, and hides other people's:
    // an unfinished inspection is the most actionable row on this screen for
    // the one person who can reopen it, and noise for everyone else. Naming
    // the statuses instead (`draft,submitted`) would hardcode the two that
    // exist today and silently drop a third.
    // `r.data`, not `r`. unwrapArray takes the axios BODY; handed the response
    // it finds a `data` key, treats the response as the envelope, and returns
    // [] — an empty list, which on THIS screen reads as "your inspections are
    // gone". That is the exact complaint being fixed, produced by the fix.
    queryFn: () => api.casingInspections.list({ scope: 'crew' }).then((r: any) => unwrapArray<any>(r.data)),
  });

  const submissions: any[] = data ?? [];
  useEffect(() => {
    if (isError) console.error('[FieldInspectionsScreen] inspections read failed:', (error as any)?.response?.data?.error ?? error);
  }, [isError, error]);

  const openRow = (row: any) => {
    // A draft reopens in the form it was written in; a submitted inspection is
    // a record, and opens read-only.
    navigate(
      isResumableDraft(row)
        ? inspectionResumePath(row.id)
        : inspectionDetailPath('/field', row.id),
    );
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          {/* Goes to /field, not back through history — see the same control on
              FieldFormsScreen. The home screen is the only navigation in this
              tree now, so reaching it must not depend on what happens to be on
              the history stack. */}
          <button onClick={goBack} aria-label="Back" className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <h1 className="text-xl font-bold text-white">Inspections</h1>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4">
        {/* Says whose work is here, because the answer is no longer "yours".
            A hand who reads this as their own list and sees a crewmate's row
            will read it as someone filing under their name. */}
        <p className="text-xs text-fieldiq-text">
          Inspections on your jobs, plus your own and anything you have not finished.
        </p>

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 size={28} className="animate-spin text-fieldiq-text" />
          </div>
        )}

        {/* A failed read is not an empty list. "No inspections yet" on a dropped
            request tells a hand their work is gone — which is exactly the
            complaint this screen exists to answer — and they would stop
            looking. It also must not offer the New button below: turning a
            read failure into an invitation to file a second copy of an
            inspection they already filed is worse than the blank screen. */}
        {isError && !isLoading && (
          <div className="mt-5 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {/* Fixed copy, never the API's sentence: a refusal reads
                "casing_running vertical not enabled for this company",
                which tells a hand nothing. The detail goes to the console. */}
            Could not load your inspections.
          </div>
        )}

        {!isLoading && !isError && submissions.length === 0 && (
          <div className="text-center py-12">
            <ClipboardCheck size={40} className="mx-auto text-fieldiq-text/40 mb-3" />
            <p className="text-sm text-fieldiq-text">No inspections on your jobs yet</p>
            <button
              onClick={() => navigate('/field/safety/inspection')}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-fieldiq-accent text-fieldiq-dark text-sm font-semibold min-h-[44px]"
            >
              <Plus size={16} /> New Inspection
            </button>
          </div>
        )}

        {!isLoading && !isError && submissions.length > 0 && (
          <div className="mt-5 space-y-3">
            {submissions.map((row) => {
              const statusLabel = inspectionStatusLabel(row);
              const isDraft = isResumableDraft(row);
              // inspection_date is a calendar day; created_at is an instant.
              // They are formatted by different helpers on purpose — parsing
              // the calendar day as an instant renders it a day early west of
              // Greenwich, which is every tenant on this app. The instant is
              // shown as the TENANT's day (companies.time_zone).
              const day = row.inspection_date
                ? formatCalendarDate(row.inspection_date)
                : row.created_at
                  ? (formatInstantDay(row.created_at, timeZone) ?? '—')
                  : '—';
              // The ASSET's own identity first. `tong_make`/`tong_model`/
              // `serial_number` are free text an inspector typed, so on a
              // crew-wide list two hands describe one tool three ways — and
              // the question here is whether a particular piece of gear was
              // checked. The typed text stays as the fallback for the rows
              // that have no equipment_id (8 of 55 in production at the time
              // the API side was measured).
              const tool =
                row.equipment_name ||
                row.equipment_unit_number ||
                (row.tong_make || row.tong_model
                  ? [row.tong_make, row.tong_model].filter(Boolean).join(' ')
                  : row.serial_number) ||
                '—';
              // Absent renders as ABSENT. Three of the six doors into the form
              // never know a job, so a job-less inspection is a permanent and
              // ordinary state — not a gap. An em dash or a "No job" label
              // would mark half the rows on this screen as incomplete forever.
              const jobLabel = row.job_number || null;
              // Whose it is. Only shown for someone else's: on your own row it
              // is your own name, which is noise, and its absence is what
              // makes a crewmate's row legible at a glance.
              const filedBy = row.submitted_by === currentUserId ? null : row.submitted_by_name || null;

              return (
                <button
                  key={row.id}
                  data-testid={`inspection-row-${row.id}`}
                  onClick={() => openRow(row)}
                  className="w-full text-left bg-fieldiq-card rounded-xl p-4 card-hover flex items-center gap-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{inspectionFormTypeLabel(row)}</span>
                      <span
                        className={
                          'text-[11px] px-2 py-0.5 rounded-full border ' +
                          (isDraft
                            ? 'border-fieldiq-accent/50 text-fieldiq-accent bg-fieldiq-accent/10'
                            : 'border-fieldiq-border text-fieldiq-muted')
                        }
                      >
                        {statusLabel}
                      </span>
                    </div>
                    <p className="text-xs text-fieldiq-text mt-1 truncate">{tool}</p>
                    <p className="text-xs text-fieldiq-muted mt-0.5">
                      {day}
                      {/* A draft has no finding yet, so it shows none rather
                          than an em dash implying one is missing. */}
                      {!isDraft && <> · {inspectionResultLabel(row)}</>}
                      {/* Each of these is rendered only when present, never as
                          a placeholder — see jobLabel/filedBy above. */}
                      {jobLabel && <> · {jobLabel}</>}
                      {filedBy && <> · {filedBy}</>}
                    </p>
                  </div>
                  <ChevronRight size={18} className="text-fieldiq-muted shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
